import { NotificationPermissionPage } from '@psl/appComponents/notificationModal/NotificationModal';
import BackIcon from '@psl/assets/back.svg';
import { EditingListingFlow } from '@psl/flows/catalog/EditingListingFlow';
import EmbeddedWebviewPage from '@psl/flows/common/embedded-webview/EmbeddedWebviewPage';
import { AlertScreen } from '@psl/flows/common/ux/AlertScreen';
import { ConfirmScreen } from '@psl/flows/common/ux/ConfirmScreen';
import { PromptScreen } from '@psl/flows/common/ux/PromptScreen';
import { YouTubeVideoPage } from '@psl/flows/common/youtube/YouTubeVideoPage';
import { CommunityPage } from '@psl/flows/community/CommunityPage';
import { UserFrontFlow } from '@psl/flows/me';
import { OrdersFlow } from '@psl/flows/orders/OrdersFlow';
import { OrderStatusUpdatedPage } from '@psl/flows/orders/pages/OrderStatusUpdatedPage';
import { ContinuousOnboardingPostItemPage } from '@psl/flows/seller-onboarding/pages/ContinuousOnboardingPostItemPage';
import { SellerOnboardingFlow } from '@psl/flows/seller-onboarding/SellerOnboardingFlow';
import { SlackLoginPage } from '@psl/flows/user/SlackLoginPage';
import { isDebugEnabled } from '@psl/functions/debugging';
import {
  ScreenToParams,
  spreadScreens,
} from '@psl/functions/navigation/utils/wrapNavigatorScreen';
import { makeDebug } from '@psl/utils/makeDebug';
import WalletFlowPage from '@psl/wallet/WalletFlowPage';
import { createStackNavigator } from '@react-navigation/stack';
import { memo } from 'react';
import PopClipFlow from './addNewClip';
import CameraScreenPage from './camera/CameraScreenPage';
import MediaPreviewPage from './camera/MediaPreviewPage';
import { CommentChatStartupPage } from './commentchat';
import CreateCommentChatFlow from './commentchat/creation/CreateCommentChatFlow';
import { CommentChatOnboardingFlow } from './commentchat/onboarding/CommentChatOnboardingFlow';
import CountrySelectorPage from './common/address/pages/CountrySelectorPage';
import { StateSelectPage } from './common/address/pages/StateSelectPage';
import { AppBottomSheetFlow } from './common/app-bottomsheet-flow';
import { UserBadgePage } from './common/badges/UserBadgePage';
import ConfirmationPage from './common/confirmation/ConfirmationPage';
import { SharedReviewPage } from './common/order-message/SharedReviewPage';
import { ChatPage } from './community/chat/pages/chat-room/ChatPage';
import { ChatUserSearchPage } from './community/chat/pages/search/SearchPage';
import DebugPanelPage from './debugging/DebugPanel/DebugPanelPage';
import { OtaUpdatesLogsPage } from './debugging/OTAUpdateLogs/OtaUpdatesLogsPage';
import { DiscoveryFeaturedEventListPage } from './discovery/content-providers/featured-events/list';
import { DiscoveryFeaturedStoreListPage } from './discovery/content-providers/featured-stores/list';
import { DiscoveryCategoryPage } from './discovery/DiscoveryCategoryPage';
import EventDiscoveryPageV2 from './discovery/EventDiscoveryPageV2';
import GoLiveFlow from './golive/GoLiveFlow';
import { MainPage } from './main/MainPage';
import GuideToDownloadAppPage from './misc/download-app-guide/GuideToDownloadAppPage';
import { PrivacyPolicyScreen } from './misc/privacy-policy/PrivacyPolicyScreen';
import { TermsOfServiceScreen } from './misc/terms-of-service/TermsOfServiceScreen';
import { isOnboardingPhaseInterruptible } from './onboarding/functions/isOnboardingPhaseInterruptible';
import { useOnboardingContext } from './onboarding/OnboardingContext';
import { OnboardingPage } from './onboarding/OnboardingPage';
import PatreonFlowPage from './patreon/PatreonFlowPage';
import { StoreListingDetailPage } from './pdp/StoreListingDetailPage';
import ProfilePage from './profiles/ProfilePage';
import { ReferralGiftingClaimGiftFlowPage } from './referralGifting/ReferralGiftingClaimGiftFlowPage';
import { ReferralGiftingSendGiftFlowPage } from './referralGifting/ReferralGiftingSendGiftFlowPage';
import { ReferralGiftRouteEntryPage } from './referralGifting/ReferralGiftRouteEntryPage';
import ScheduleUpcomingShowFlow from './scheduleUpcomingShow/ScheduleUpcomingShowFlow';
import { SearchPage } from './search/SearchPage';
import SellerApplicationFlow from './seller-application';
import CategoriesConfirmPage from './settings/components/CategoriesConfirmPage';
import { ReclaimReferralPage } from './settings/pages/ReferralsPage/ReclaimReferralPage/ReclaimReferralPage';
import SettingsFlow from './settings/SettingsFlow';
import { SharePage } from './share/SharePage';
import SharePlayPage from './shareplay/SharePlayPage';
import { ShopSearchPage } from './shop/ShopSearchPage';
import ShowCasePage from './showcase/ShowCasePage';
import { StoreFrontFlow } from './storeFront';
import TutorialsPage from './tutorials/TutorialsPage';
import AuthFlowPage from './user/AuthFlowPage';
import OnboardingFlowPage from './user/OnboardingFlowPage';
import { DeleteMyAccountPage } from './user/pages/DeleteMyAccountPage';
import AnyVideoClipsPage from './videoClips/AnyVideoClipsPage';
import { CSClipsPage } from './videoClips/CSClipsPage';
import { SharedPopClipPage } from './videoClips/SharedPopClipPage';
import { ShopifyClipsPage } from './videoClips/ShopifyVideoClipPage';

const debug = makeDebug('routes', isDebugEnabled('routes'));

const Screens = {
  // routes for internal use only
  _otaUpdatesLogs: OtaUpdatesLogsPage,
  debugPanel: DebugPanelPage,
  slacklogin: SlackLoginPage,

  home: MainPage,
  search: SearchPage,
  me: UserFrontFlow,
  storefront: StoreFrontFlow,
  onboarding: OnboardingPage,
  messages: CommunityPage,

  showcase: ShowCasePage,
  shareplay: SharePlayPage,
  _categoriesConfirm: CategoriesConfirmPage,
  categoryDiscovery: DiscoveryCategoryPage,

  scheduleShow: ScheduleUpcomingShowFlow,
  createPopClip: PopClipFlow,
  editPopClip: PopClipFlow,
  goLive: GoLiveFlow,

  review: SharedReviewPage,
  popclip_v1: SharedPopClipPage,
  shopifyClips: ShopifyClipsPage,
  csClips: CSClipsPage,
  profile: ProfilePage,
  sellerProfile: ProfilePage,
  userProfile: ProfilePage,
  storeListingDetail: StoreListingDetailPage,

  becomeASeller: SellerApplicationFlow,
  setupCommentChat: CommentChatOnboardingFlow,
  createCommentChat: CreateCommentChatFlow,
  editCommentChat: CreateCommentChatFlow,
  startCommentChat: CommentChatStartupPage,

  // less frequently used routes
  deleteMyAccount: DeleteMyAccountPage,
  userBadge: UserBadgePage,
  termsOfService: TermsOfServiceScreen,
  privacyPolicy: PrivacyPolicyScreen,
  referralGifting: ReferralGiftRouteEntryPage,

  // discovery v2 specific list pages
  featuredEvents: DiscoveryFeaturedEventListPage,
  featuredStores: DiscoveryFeaturedStoreListPage,
  discoveryEvent: EventDiscoveryPageV2,
  settings: SettingsFlow,

  // orders
  ordersFlow: OrdersFlow,
  _orderStatusUpdatedNotification: OrderStatusUpdatedPage,

  // anything start with _ will not be saved in URL redirect
  _sellerOnboarding: SellerOnboardingFlow,
  _editListing: EditingListingFlow,
  _state_select: StateSelectPage,
  _country_select: CountrySelectorPage,
  _auth: AuthFlowPage,
  _wallet: WalletFlowPage,
  _patreon: PatreonFlowPage,
  _onboarding: OnboardingFlowPage,
  _camera: CameraScreenPage,
  _mediaPreview: MediaPreviewPage,
  _guideToDownloadApp: GuideToDownloadAppPage,
  _notificationPermission: NotificationPermissionPage,
  _share: SharePage,
  _anyVideoClips: AnyVideoClipsPage,
  _confirmation: ConfirmationPage,
  _tutorials: TutorialsPage,

  _reclaimReferral: ReclaimReferralPage,
  _referralGiftingSendGift: ReferralGiftingSendGiftFlowPage,
  _referralGiftingClaimGift: ReferralGiftingClaimGiftFlowPage,

  _appBottomSheet: AppBottomSheetFlow,
  _shopSearch: ShopSearchPage,

  _chat: ChatPage,
  _searchChatUsers: ChatUserSearchPage,
  _embeddedWebviewPage: EmbeddedWebviewPage,
  _continuousOnboardingPostItem: ContinuousOnboardingPostItemPage,
  _youTubeVideo: YouTubeVideoPage,

  // ux related
  _prompt: PromptScreen,
  _alert: AlertScreen,
  _confirm: ConfirmScreen,
};

// @ts-ignore
export type AppModalParams = ScreenToParams<typeof Screens>;

const Stack = createStackNavigator<AppModalParams>();

const Routes = memo(() => {
  const { initialUserId, authenticationRequired, initialProgress } =
    useOnboardingContext();

  let initialRouteName: keyof AppModalParams = 'home';

  // Enter the new onboarding flow if the following criterion is met:
  // 1. authentication if required (determined by device type and a/b testing)
  // 2. one of the following cases:
  //   2.1 the user is not logged in yet
  //   2.2 the user has perviously logged in while onboarding but has
  //       not finished the required (non-interruptible) steps yet.
  if (
    authenticationRequired &&
    (initialUserId == null ||
      (initialProgress != null &&
        !isOnboardingPhaseInterruptible(initialProgress.phase.type)))
  ) {
    initialRouteName = 'onboarding';
  }

  debug('initial route name: %s', initialRouteName);

  return (
    <Stack.Navigator
      initialRouteName={initialRouteName}
      screenOptions={{
        headerShown: true,
        headerBackTitle: ' ',
        detachPreviousScreen: false,
        headerBackImage: () => <BackIcon />,
      }}
    >
      {spreadScreens(Stack.Screen, Screens as any)}
    </Stack.Navigator>
  );
});

export default Routes;
